/**
 * Fusion — cart
 *
 * AJAX cart built on Shopify's native Cart API and Section Rendering API.
 *
 * Flow for every mutation:
 *   1. POST to the cart endpoint
 *   2. GET the cart drawer section back as rendered HTML
 *   3. Swap the drawer contents and sync the header count
 *
 * Emits `cart:updated` on document so any component can react.
 */

(function () {
  'use strict';

  const routes = window.Fusion.routes;
  const strings = window.Fusion.strings;

  /* ======================================================================
     Cart API
     ====================================================================== */

  async function request(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      // Shopify returns a description on 4xx (out of stock, quantity rules)
      throw new Error(data.description || data.message || strings.cartError);
    }

    return data;
  }

  const cart = {
    async add(items) {
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      const result = await request(routes.cartAdd, payload);
      await refresh();
      return result;
    },

    async change(line, quantity) {
      const result = await request(routes.cartChange, { line, quantity });
      await refresh(result);
      return result;
    },

    async updateNote(note) {
      return request(routes.cartUpdate, { note });
    },

    async get() {
      const response = await fetch(`${routes.cart}.js`);
      return response.json();
    },
  };

  /* ======================================================================
     Re-render
     ====================================================================== */

  /**
   * Pulls fresh drawer markup from the Section Rendering API and swaps it in.
   * Falls back to a full page reload if the section cannot be fetched, so the
   * customer never ends up looking at a stale cart.
   */
  async function refresh() {
    const drawer = document.querySelector('[data-cart-drawer]');

    // Cart page (or drawer disabled) — reload so server-rendered totals are right
    if (!drawer) {
      if (document.body.classList.contains('template-cart')) window.location.reload();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return;
    }

    const sectionId = drawer.dataset.sectionId;

    try {
      const response = await fetch(`${routes.cart}?sections=${encodeURIComponent(sectionId)}`);
      if (!response.ok) throw new Error('Section fetch failed');

      const sections = await response.json();
      const html = sections[sectionId];
      if (!html) throw new Error('Section missing from response');

      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-cart-drawer]');

      if (!fresh) throw new Error('Drawer missing from rendered section');

      const wasOpen = drawer.open;

      drawer.innerHTML = fresh.innerHTML;
      drawer.dataset.count = fresh.dataset.count;

      // innerHTML replacement drops both the listeners and the open-state classes
      drawer.bindPanel();
      drawer.bindCartControls();

      if (wasOpen) {
        drawer.classList.add('is-open');
        drawer.overlay?.classList.add('is-active');
      }

      syncCount(parseInt(fresh.dataset.count, 10) || 0);

      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('[Fusion] Cart refresh failed, reloading.', error);
      window.location.reload();
    }
  }

  // Tracks the last known count so a bump only plays when it goes up —
  // a removal or quantity decrease shouldn't feel like a celebratory add.
  let lastKnownCartCount = null;

  function syncCount(count) {
    const increased = lastKnownCartCount !== null && count > lastKnownCartCount;
    lastKnownCartCount = count;

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);

      if (increased) {
        el.classList.remove('is-bumping');
        void el.offsetWidth; // Restarts the animation on back-to-back adds
        el.classList.add('is-bumping');
      }
    });
  }

  /* ======================================================================
     Cart drawer
     ====================================================================== */

  class FusionCartDrawer extends window.FusionDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.bindCartControls();

      // Opened by other components after a successful add to cart
      document.addEventListener('cart:open', () => this.show());
    }

    /** Binds the cart-specific controls. Re-run after every re-render. */
    bindCartControls() {
      this.querySelectorAll('[data-cart-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          this.setBusy(true);
          cart.change(parseInt(button.dataset.line, 10), 0)
            .then(() => window.Fusion.announce(strings.addedToCart))
            .catch((error) => this.showError(error.message))
            .finally(() => this.setBusy(false));
        });
      });

      const note = this.querySelector('[data-cart-note]');
      note?.addEventListener(
        'change',
        window.Fusion.debounce((event) => cart.updateNote(event.target.value), 500)
      );

    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.querySelectorAll('button, input').forEach((el) => {
        el.disabled = busy;
      });
    }

    showError(message) {
      window.Fusion.announce(message, true);

      let error = this.querySelector('[data-cart-error]');
      if (!error) {
        error = document.createElement('p');
        error.className = 'cart-drawer__error field__error';
        error.setAttribute('role', 'alert');
        error.dataset.cartError = '';
        this.querySelector('[data-drawer-panel]')?.prepend(error);
      }
      error.textContent = message;
    }

    initialFocusTarget() {
      return this.querySelector('[data-drawer-close]');
    }
  }

  if (!customElements.get('fusion-cart-drawer')) {
    customElements.define('fusion-cart-drawer', FusionCartDrawer);
  }

  /* ======================================================================
     Quantity input
     ====================================================================== */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-quantity-input]');
      if (!this.input) return;

      this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.step(1));

      // Debounced so holding a button or typing does not fire a request per keystroke
      this.input.addEventListener('change', window.Fusion.debounce(() => this.commit(), 400));
    }

    step(direction) {
      const increment = parseInt(this.input.step, 10) || 1;
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;

      const next = (parseInt(this.input.value, 10) || 0) + increment * direction;
      this.input.value = Math.min(Math.max(next, min), max);

      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    commit() {
      const line = this.input.dataset.line;

      // Only cart line items commit to the server; product form inputs do not
      if (!line) return;

      const quantity = parseInt(this.input.value, 10);
      if (Number.isNaN(quantity) || quantity < 0) {
        window.Fusion.announce(strings.quantityError, true);
        return;
      }

      cart.change(parseInt(line, 10), quantity).catch((error) => {
        window.Fusion.announce(error.message, true);
      });
    }
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* ======================================================================
     Public API
     ====================================================================== */

  window.Fusion.cart = cart;
  window.Fusion.refreshCart = refresh;
})();
